import {
  Available,
  Lock,
  PreBookingBlock,
  Purchase,
  TableBooking,
  TableBookingPurchase,
  TableBookingWalkIn,
  TableHold,
  WalkIn,
} from 'generated/graphql';
import {
  BlockedTableStateRange,
  SelectedAvailableRange,
  SelectedSlot,
  SelectedTableStateRange,
  SelectedTimelineItem,
  TableStateRange,
  TableStateRangeFragmentEitherType,
} from 'types/tableBasedInventoryRangeVariants';
import {
  LockedCalendarSquare,
  NormalDetailedSquare,
  WalkInCalendarSquare,
} from 'types/tableBasedInventory';

// TableStateRange guards
export const isTableBooking = (
  tableStateRange?: TableStateRangeFragmentEitherType
): tableStateRange is TableBooking => {
  return (
    tableStateRange?.__typename === 'TableBookingPurchase' ||
    tableStateRange?.__typename === 'TableBookingWalkIn'
  );
};

export const generatesPreBookingBlock = (tableStateRange?: TableStateRangeFragmentEitherType) => {
  return isTableBooking(tableStateRange) || isLock(tableStateRange);
};

export const isTableBookingPurchase = (
  tableStateRange: TableStateRange | undefined
): tableStateRange is TableBookingPurchase => {
  return tableStateRange?.__typename === 'TableBookingPurchase';
};

export const isLock = (
  tableStateRange?: TableStateRangeFragmentEitherType
): tableStateRange is Lock => {
  return (
    tableStateRange?.__typename === 'GuestLock' || tableStateRange?.__typename === 'InviteLock'
  );
};

export const isHold = (
  tableStateRange?: TableStateRangeFragmentEitherType
): tableStateRange is TableHold => {
  return tableStateRange?.__typename === 'TableHold';
};

export const isBlock = (
  tableStateRange?: TableStateRangeFragmentEitherType
): tableStateRange is BlockedTableStateRange => {
  return (
    tableStateRange?.__typename === 'Block' ||
    tableStateRange?.__typename === 'PacingBlock' ||
    tableStateRange?.__typename === 'InactiveTimeBlock'
  );
};

export const isAvailable = (
  tableStateRange?: TableStateRangeFragmentEitherType
): tableStateRange is Available => {
  return tableStateRange?.__typename === 'Available';
};

export const isPrebookingBlock = (
  tableStateRange?: TableStateRangeFragmentEitherType
): tableStateRange is PreBookingBlock => {
  return tableStateRange?.__typename === 'PreBookingBlock';
};

export const isBlockingOverlapping = (
  tableStateRange?: TableStateRangeFragmentEitherType
): tableStateRange is BlockedTableStateRange => {
  return isBlock(tableStateRange);
};

export const isWalkIn = (
  tableStateRange: TableStateRange | undefined
): tableStateRange is TableBookingWalkIn => {
  return tableStateRange?.__typename === 'TableBookingWalkIn';
};

// Selectable TimelineItem guards
export const isSelectedTableStateRange = (
  selectedTimelineItem: SelectedTimelineItem | null | undefined
): selectedTimelineItem is SelectedTableStateRange => {
  return !!selectedTimelineItem && 'rangeType' in selectedTimelineItem;
};

export const isSelectedSlot = (
  selectedTimelineItem: SelectedTimelineItem | null | undefined
): selectedTimelineItem is SelectedSlot => {
  return !!selectedTimelineItem && 'slotType' in selectedTimelineItem;
};

export const isSelectedAvailableRange = (
  tableStateRange: SelectedTimelineItem | null
): tableStateRange is SelectedAvailableRange => {
  return isSelectedTableStateRange(tableStateRange) && tableStateRange.rangeType === 'AVAILABLE';
};

// Booking type guards
export type AbstractBooking = Purchase | WalkIn;

export const bookingIsPurchase = (booking: AbstractBooking | undefined): booking is Purchase => {
  return booking?.__typename === 'Purchase';
};

export const bookingIsWalkIn = (booking: AbstractBooking | undefined): booking is WalkIn => {
  return booking?.__typename === 'WalkIn';
};

// Legacy type guards to work with Selector Soup types
export const squareIsWalkInSquare = (
  square: NormalDetailedSquare | undefined
): square is WalkInCalendarSquare => {
  return square?.subType === 'WALK_IN';
};

export const squareIsLockedSquare = (
  square: NormalDetailedSquare | undefined
): square is LockedCalendarSquare => {
  return square?.subType === 'LOCKED';
};
